import React, { createContext, useContext, useState, useEffect } from 'react'
import type { User } from '../types/@types/user'
import { login as loginService, getMe, logout as logoutService } from '../services/AuthService'

interface AuthContextValue {
  token: string | null
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem('authToken')
    if (!stored || stored === 'null' || stored === 'undefined') return null
    return stored
  })
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('authUser')
    if (!stored || stored === 'null' || stored === 'undefined') return null
    try {
      return JSON.parse(stored) as User
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(!!token)

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          // Si hay un token, validarlo inmediatamente con el backend
          const userData = await getMe()
          setUser(userData)
        } catch (error: any) {
          // Solo cerramos sesión si el servidor nos dice explícitamente que el token es inválido
          if (error.response && [401, 403].includes(error.response.status)) {
            console.error('Sesión inválida, cerrando sesión...')
            setToken(null)
            setUser(null)
          } else {
            // Si es un error de red o de servidor (500), mantenemos el estado actual
            // permitiendo que la PWA funcione con los datos en caché (ya cargados en el estado inicial)
            console.warn('Error al verificar token (posible falta de conexión o error de servidor):', error.message)
          }
        } finally {
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    }
    
    verifyToken()
  }, [])

  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token)
    } else {
      localStorage.removeItem('authToken')
    }
  }, [token])

  useEffect(() => {
    if (user) {
      localStorage.setItem('authUser', JSON.stringify(user))
    } else {
      localStorage.removeItem('authUser')
    }
  }, [user])

  const login = async (email: string, password: string) => {
    const res = await loginService(email, password)
    setToken(res.token)
    setUser(res.user)
  }

  const logout = async () => {
    // 1. Limpiamos el estado local inmediatamente para reacción instantánea de la UI
    const currentToken = token
    setToken(null)
    setUser(null)
    localStorage.removeItem('authToken')
    localStorage.removeItem('authUser')
    
    // 2. Intentamos limpiar el token en el servidor de forma asíncrona
    try {
      if (currentToken) {
        await logoutService()
      }
    } catch (error) {
      console.warn('Silent logout error (likely already invalid or offline):', error)
    }
  }

  return (
    <AuthContext.Provider value={{ token, user, login, logout, isLoading: loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
